import { QueryClient } from '@tanstack/react-query';

/**
 * Centralized cache invalidation utility
 * Optimizes parallel invalidation and provides consistent patterns
 */
export class CacheInvalidationManager {
  constructor(private queryClient: QueryClient) {}

  /**
   * Efficiently invalidate multiple query keys in parallel
   */
  async invalidateQueries(queryKeys: unknown[][]) {
    const invalidations = queryKeys.map((queryKey) =>
      this.queryClient.invalidateQueries({ queryKey })
    );
    return Promise.all(invalidations);
  }

  /**
   * Invalidate experiment-related queries
   */
  async invalidateExperimentQueries(experimentId?: string) {
    const keys: unknown[][] = [['experiments'], ['experiments-analytics']];

    if (experimentId) {
      keys.push(['experiments', 'detail', experimentId]);
    }

    return this.invalidateQueries(keys);
  }

  /**
   * Invalidate hypothesis-related queries
   */
  async invalidateHypothesisQueries(hypothesisId?: string) {
    const keys: unknown[][] = [['hypotheses'], ['experiments-analytics']];

    if (hypothesisId) {
      keys.push(['hypotheses', 'detail', hypothesisId]);
    }

    return this.invalidateQueries(keys);
  }

  /**
   * Invalidate experiment-hypothesis relationship queries
   */
  async invalidateExperimentHypothesesQueries(
    experimentId?: string,
    hypothesisId?: string
  ) {
    const keys: unknown[][] = [
      ['experiments'],
      ['hypotheses'],
      ['experiments-analytics'],
    ];

    if (experimentId) {
      keys.push(
        ['experiments', 'hypotheses', experimentId],
        ['experiments', 'hypotheses', 'raw', experimentId]
      );
    }

    if (hypothesisId) {
      keys.push(['hypothesis-experiments', hypothesisId]);
    }

    return this.invalidateQueries(keys);
  }

  /**
   * Invalidate project-related queries
   */
  async invalidateProjectQueries(projectId?: string) {
    const keys: unknown[][] = [['projects']];

    if (projectId) {
      keys.push(
        ['projects', 'detail', projectId],
        ['projects', 'overview', projectId],
        ['projects', 'measures', projectId],
        ['projects', 'launch-plan', projectId],
        ['projects', 'experiments', projectId]
      );
    }

    return this.invalidateQueries(keys);
  }
}

/**
 * Factory function to create invalidation manager instance
 */
export function createCacheInvalidationManager(queryClient: QueryClient) {
  return new CacheInvalidationManager(queryClient);
}
